import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import _ from 'lodash'
import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { Layout } from '@/components/Layout'
import { MultiSelect } from '@/components/MultiSelect'
import { Select } from '@/components/Select'
import { getEntityRoles, getEntityJobs, deleteEntityRole } from '@/services/entity.service'
import { UserStoreContext } from '@/stores/UserStore'
import { ACTIVE_USER_STATUS_OPTIONS, ROLE_OPTIONS } from '@/utils/constants'
import { hasAnyFilterSelected, toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import AddEditUserSlideout from './AddEditUserSlideout'

const DEFAULT_FILTERS = {
  role: [],
  hasLoggedIn: null,
}

/**
 * Accounts / Users
 *
 * This component displays a list of users for a given entity. It's accessible to admins and
 * owners of the entity only.
 *
 * @note We call the entities service for users because we can expand the user object from the
 * entity roles. This is because permissions are already scoped to the entity for our current user.
 *
 */
const Users = observer(() => {
  const { entityId } = useParams()
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [sorting, setSorting] = useState([{ id: 'user__last_name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showUserSlideout, setShowUserSlideout] = useState(false)
  const [editUser, setEditUser] = useState(null)

  // Context
  const { user } = useContext(UserStoreContext)

  // Filter or search changes shrink the result set, so deep pages can 404 from
  // the paginator. Reset to the first page in the same handler as the filter
  // change so React 18 batches both updates into a single render.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const queryKey = ['accounts', 'users', entityId, sorting, pagination, searchTerm, filters]

  // GET users
  const { data: users, isPending } = useQuery({
    queryKey,
    queryFn: () =>
      getEntityRoles({
        entityId,
        sorting,
        pagination,
        searchTerm,
        filters: {
          // NOTE: filters.role is an array, but configureFilterQuery isn't built to handle arrays
          // even though the Select component for the table filter allows us to select multiple values
          role: filters.role[0] || DEFAULT_FILTERS.role[0],
          has_logged_in: filters.hasLoggedIn,
        },
      }),
    placeholderData: keepPreviousData,
  })

  // GET jobs for the entity (for multiselect options)
  const { data: jobsData } = useQuery({
    queryKey: ['accounts', 'jobs', entityId],
    queryFn: () => getEntityJobs({ entityId }),
  })
  const jobs = jobsData?.results || []

  // DELETE user
  const { isPending: pendingDelete, mutate: deleteUser } = useMutation({
    mutationFn: (data) => deleteEntityRole({ entityId, id: data }),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('User deleted successfully')

      // Refresh accounts users list
      queryClient.invalidateQueries({ queryKey })
    },
  })

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current
  return (
    <Layout breadcrumbs={['Admin', 'Users']}>
      <Layout.Heading title="Users" />

      {/* @fixme: This is a hacky way to make the table filters responsive */}
      <div className="flex flex-row items-center gap-2">
        {(searchTerm || hasAnyFilterSelected(filters)) && (
          <Button
            className="visible md:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              setFilters(DEFAULT_FILTERS)
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button className="visible md:hidden" onClick={() => setShowUserSlideout(true)}>
          Add User
        </Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for User"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />

          <MultiSelect
            placeholder="Filter by Role"
            options={ROLE_OPTIONS}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                role: value || [],
              }))
              resetToFirstPage()
            }}
            value={filters.role}
            width="w-full md:w-56"
          />

          <Select
            className="w-full md:w-56"
            placeholder="Filter by Active Users"
            options={ACTIVE_USER_STATUS_OPTIONS}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                hasLoggedIn: value,
              }))
              resetToFirstPage()
            }}
            value={filters.hasLoggedIn}
          />

          {(searchTerm || hasAnyFilterSelected(filters)) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setFilters(DEFAULT_FILTERS)
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <div className="hidden gap-2 md:flex">
          <Button onClick={() => setShowUserSlideout(true)}>Add User</Button>
        </div>
      </div>

      <DataTable
        columns={[
          {
            accessorFn: (student) => formatUserName(student.user),
            id: 'user__last_name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <Link
                to={`/accounts/${entityId}/users/${row.original.id}`}
                className={`cursor-pointer text-left hover:underline ${!row.original.user.lastLogin ? 'text-muted-foreground' : 'text-action'}`}
              >
                {formatUserName(row.original.user)}
              </Link>
            ),
          },
          {
            accessorKey: 'user__email',
            header: ({ column }) => <SortableColumnHeader column={column} label="Email" />,
            cell: ({ row }) => (
              <span className={!row.original.user.lastLogin ? 'text-muted-foreground' : ''}>
                {row.original.user.email}
              </span>
            ),
            size: 280,
          },
          {
            accessorKey: 'roles',
            id: 'roles',
            header: ({ column }) => <SortableColumnHeader column={column} label="Roles" />,
            cell: ({ row }) => <span>{row.original.roles?.join(', ') || ''}</span>,
            size: 280,
          },
          {
            accessorKey: 'jobs',
            id: 'jobs',
            header: 'Jobs',
            cell: ({ row }) => (
              <span>{row.original.jobs?.map((j) => j.title).join(', ') || ''}</span>
            ),
          },
          {
            accessorKey: 'user__last_login',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Login" />,
            cell: ({ row }) => (
              <span>
                {row.original.user.lastLogin
                  ? dayjs(row.original.user.lastLogin).format('MMM D, YYYY h:mm A')
                  : 'N/A'}
              </span>
            ),
          },
          {
            accessorKey: 'user__last_activity',
            header: ({ column }) => <SortableColumnHeader column={column} label="Last Activity" />,
            cell: ({ row }) => (
              <span>
                {row.original.user.lastActivity
                  ? dayjs(row.original.user.lastActivity).format('MMM D, YYYY h:mm A')
                  : 'N/A'}
              </span>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditUser(row.original)
                  setShowUserSlideout(true)
                }}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
            ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete: ${formatUserName(row.original.user)}?`}
                confirmTitle="Delete User"
                className="place-self-end"
                variant="destructive"
                size="sm"
                loading={pendingDelete}
                onClick={() => deleteUser(row.original.id)}
                disabled={row.original.user.id === user?.id}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete user</span>
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={users?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={users?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showUserSlideout && (
        <AddEditUserSlideout
          editUser={editUser}
          entityId={entityId}
          jobs={jobs}
          queryKey={queryKey}
          resetAndClose={() => {
            setShowUserSlideout(false)
            setEditUser(null)
            queryClient.invalidateQueries({ queryKey: ['accounts', 'users', entityId] })
          }}
        />
      )}
    </Layout>
  )
})

export default Users
